export type CookiePreferences = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
};

export const COOKIE_PREFERENCES_KEY = 'cookiePreferences';
export const LEGACY_COOKIE_CONSENT_KEY = 'cookieConsent';
export const COOKIE_PREFERENCES_UPDATED_EVENT = 'trenio:cookie-preferences-updated';
export const OPEN_COOKIE_BANNER_EVENT = 'trenio:open-cookie-banner';

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

export const buildCookiePreferences = (analytics: boolean, marketing: boolean): CookiePreferences => ({
  essential: true,
  analytics,
  marketing,
  updatedAt: new Date().toISOString(),
});

export const parseStoredCookiePreferences = (value: string | null): CookiePreferences | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<CookiePreferences>;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.marketing !== 'boolean') {
      return null;
    }

    return {
      essential: true,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
};

export const getStoredCookiePreferences = (): CookiePreferences | null => {
  if (!canUseStorage()) {
    return null;
  }

  return parseStoredCookiePreferences(window.localStorage.getItem(COOKIE_PREFERENCES_KEY));
};

export const saveCookiePreferences = (preferences: CookiePreferences) => {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
  window.localStorage.setItem(
    LEGACY_COOKIE_CONSENT_KEY,
    preferences.analytics || preferences.marketing ? 'accepted' : 'declined'
  );

  window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_UPDATED_EVENT, { detail: preferences }));
};

export const loadCookiePreferencesWithLegacyMigration = (): CookiePreferences | null => {
  const stored = getStoredCookiePreferences();
  if (stored) {
    return stored;
  }

  if (!canUseStorage()) {
    return null;
  }

  const legacyConsent = window.localStorage.getItem(LEGACY_COOKIE_CONSENT_KEY);
  if (legacyConsent !== 'accepted' && legacyConsent !== 'declined') {
    return null;
  }

  const migrated =
    legacyConsent === 'accepted'
      ? buildCookiePreferences(true, true)
      : buildCookiePreferences(false, false);

  saveCookiePreferences(migrated);
  return migrated;
};
